import { format } from "date-fns";
import {
  Target,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface Goal {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  current_value: number | null;
  target_value: number | null;
  unit: string | null;
  target_date: string | null;
}

interface CarePlan {
  id: string;
  title: string;
  description: string | null;
  diagnosis: string | null;
  status: string;
  start_date: string;
  target_end_date: string | null;
  care_plan_goals: Goal[];
}

interface PatientCarePlansProps {
  carePlans: CarePlan[];
  patientId: string;
}

export function PatientCarePlans({ carePlans, patientId }: PatientCarePlansProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "on_hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "destructive";
      case "medium":
        return "default";
      default:
        return "secondary";
    }
  };

  const getGoalProgress = (goal: Goal) => {
    if (!goal.current_value || !goal.target_value) return 0;
    return Math.min((goal.current_value / goal.target_value) * 100, 100);
  };

  const activeCarePlans = carePlans.filter((cp) => cp.status === "active");
  const completedCarePlans = carePlans.filter((cp) => cp.status === "completed");

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Plans</p>
                <p className="text-2xl font-bold">{activeCarePlans.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{completedCarePlans.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Goals</p>
                <p className="text-2xl font-bold">
                  {carePlans.reduce((acc, cp) => acc + (cp.care_plan_goals?.length || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Care Plans */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Active Care Plans
            </CardTitle>
            <CardDescription>Current treatment plans and goals</CardDescription>
          </div>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Plan
          </Button>
        </CardHeader>
        <CardContent>
          {activeCarePlans.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {activeCarePlans.map((plan) => (
                <AccordionItem key={plan.id} value={plan.id} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-4 text-left">
                      <div>
                        <p className="font-semibold">{plan.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getStatusColor(plan.status)}>
                            {plan.status}
                          </Badge>
                          {plan.diagnosis && (
                            <span className="text-sm text-muted-foreground">
                              {plan.diagnosis}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-4 space-y-4">
                      {plan.description && (
                        <p className="text-muted-foreground">{plan.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Started: {format(new Date(plan.start_date), "MMM d, yyyy")}</span>
                        {plan.target_end_date && (
                          <span>
                            Target: {format(new Date(plan.target_end_date), "MMM d, yyyy")}
                          </span>
                        )}
                      </div>

                      {/* Goals */}
                      <div className="space-y-3">
                        <p className="font-medium text-sm">
                          Goals ({plan.care_plan_goals?.length || 0})
                        </p>
                        {plan.care_plan_goals && plan.care_plan_goals.length > 0 ? (
                          plan.care_plan_goals.map((goal) => (
                            <div
                              key={goal.id}
                              className="p-3 rounded-lg bg-muted/50 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{goal.title}</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant={getPriorityColor(goal.priority)}>
                                    {goal.priority}
                                  </Badge>
                                  <Badge variant="outline">{goal.status}</Badge>
                                </div>
                              </div>
                              {goal.description && (
                                <p className="text-sm text-muted-foreground">
                                  {goal.description}
                                </p>
                              )}
                              {goal.target_value && (
                                <div className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span>
                                      Progress: {goal.current_value || 0} / {goal.target_value} {goal.unit}
                                    </span>
                                    <span>{Math.round(getGoalProgress(goal))}%</span>
                                  </div>
                                  <Progress value={getGoalProgress(goal)} />
                                </div>
                              )}
                              {goal.target_date && (
                                <p className="text-xs text-muted-foreground">
                                  Target: {format(new Date(goal.target_date), "MMM d, yyyy")}
                                </p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No goals defined</p>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No active care plans</p>
              <Button variant="outline" size="sm" className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Create Care Plan
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Care Plans */}
      {completedCarePlans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Completed Care Plans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {completedCarePlans.map((plan) => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div>
                    <p className="font-medium">{plan.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(plan.start_date), "MMM yyyy")} -{" "}
                      {plan.target_end_date
                        ? format(new Date(plan.target_end_date), "MMM yyyy")
                        : "Ongoing"}
                    </p>
                  </div>
                  <Badge className={getStatusColor(plan.status)}>Completed</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
